import React from 'react';
import { ArrowDownUp } from 'lucide-react';
import { NetIcon, TennisBall } from './TennisBall';
import type { ServeState } from './useTennisScoringMatch';

interface ServeControlsProps {
  serverName: string;
  /** Doubles only — our player at net. Null hides the "At net" line and the Swap button. */
  netPlayerName: string | null;
  onSwap: () => void;
  serveState: ServeState;
  onFault: () => void;
}

/**
 * Serve strip between the two point pads: who's serving and (doubles) who's
 * at net on two labelled lines, a full-size Swap button, and the Fault button.
 * A second fault is a double fault and awards the point.
 */
export const ServeControls: React.FC<ServeControlsProps> = ({ serverName, netPlayerName, onSwap, serveState, onFault }) => {
  const isSecond = serveState === 'second';

  return (
    <div className="flex gap-2 min-h-[64px]">
      <div className="flex-1 min-w-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl px-3 py-1.5 flex flex-col justify-center gap-0.5">
        <div className="flex items-center gap-2 min-w-0">
          <TennisBall className="w-5 h-5" />
          <span className="w-14 flex-shrink-0 text-[10px] font-extrabold tracking-widest text-gray-400 dark:text-gray-500">SERVING</span>
          <span className="text-[17px] font-extrabold text-gray-900 dark:text-gray-50 truncate">{serverName}</span>
        </div>
        {netPlayerName && (
          <div className="flex items-center gap-2 min-w-0 text-gray-500 dark:text-gray-400">
            <NetIcon className="w-5 h-3.5" />
            <span className="w-14 flex-shrink-0 text-[10px] font-extrabold tracking-widest text-gray-400 dark:text-gray-500">AT NET</span>
            <span className="text-[15px] font-semibold truncate">{netPlayerName}</span>
          </div>
        )}
      </div>

      {netPlayerName && (
        <button
          type="button"
          onClick={onSwap}
          aria-label={`Swap server and net player — ${netPlayerName} is at net`}
          className="w-[60px] flex-shrink-0 rounded-2xl border border-primary/50 bg-primary/10 text-primary flex flex-col items-center justify-center gap-0.5 text-[11px] font-extrabold tracking-wide active:bg-primary/20"
        >
          <ArrowDownUp className="w-6 h-6" strokeWidth={2.2} />
          SWAP
        </button>
      )}

      <button
        type="button"
        onClick={onFault}
        aria-label={isSecond ? 'Record second serve fault (double fault)' : 'Record first serve fault'}
        className="w-[88px] flex-shrink-0 rounded-2xl border-2 border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex flex-col items-center justify-center gap-1 px-1 active:bg-red-100 dark:active:bg-red-950"
      >
        <span className="text-[15px] font-extrabold leading-none text-center">{isSecond ? 'Double fault' : 'Fault'}</span>
        <span className="flex gap-[3px]" aria-hidden>
          <span className={`w-3.5 h-1 rounded-sm bg-current ${isSecond ? '' : 'opacity-25'}`} />
          <span className="w-3.5 h-1 rounded-sm bg-current opacity-25" />
        </span>
        <span className="text-[10px] font-bold opacity-85">{isSecond ? '2nd serve' : '1st serve'}</span>
      </button>
    </div>
  );
};

export default ServeControls;
